/**
 * FlyoutPortal — portal-based flyout wrapper for config popovers.
 *
 * Renders children into `document.body` via `createPortal` so they escape
 * the `overflow-hidden` clipping of the UnifiedBottomPanel content area.
 *
 * Positioning:
 *   - Uses `position: fixed` + `getBoundingClientRect()` on the trigger ref
 *   - Appears above the trigger by default (matching `bottom-full mb-2`)
 *   - Flips to below the trigger if there isn't enough room above
 *   - Recalculates on window scroll/resize
 *
 * z-index 150 — above the bottom panel (z-50), below modals (z-[200]).
 *
 * Auto-closes on canvas pan/zoom via `useOnViewportChange` from reactflow.
 * Uses the shared `useClickOutside` hook for click-outside + Escape dismissal.
 *
 * Applies `nopan nodrag nowheel` classes + stopPropagation on pointer/click
 * to prevent ReactFlow canvas interactions from leaking through.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOnViewportChange } from 'reactflow'
import { useClickOutside } from './useClickOutside'
import { createDebug } from '@ponpon/api-client'

const portalLog = createDebug('dw:config-flyout:portal')

interface Position {
  top: number
  left: number
  flipped: boolean
}

export interface FlyoutPortalProps {
  triggerRef: React.RefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  minWidth?: number
}

function computePosition(
  triggerEl: HTMLElement,
  portalEl: HTMLElement,
  minWidth: number,
): Position {
  const rect = triggerEl.getBoundingClientRect()
  const portalHeight = portalEl.offsetHeight
  const portalWidth = Math.max(portalEl.offsetWidth, minWidth)
  const gap = 8 // mb-2 equivalent

  // Default: above the trigger
  let top = rect.top - portalHeight - gap
  let flipped = false

  // If not enough room above, flip to below
  if (top < 8) {
    top = rect.bottom + gap
    flipped = true
  }

  // Horizontal: align left edge with trigger, clamp to viewport
  let left = rect.left
  if (left + portalWidth > window.innerWidth - 8) {
    left = window.innerWidth - portalWidth - 8
  }
  if (left < 8) {
    left = 8
  }

  return { top, left, flipped }
}

export function FlyoutPortal({
  triggerRef,
  isOpen,
  onClose,
  children,
  className = '',
  minWidth = 260,
}: FlyoutPortalProps) {
  const portalRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Position | null>(null)

  // ── Position calculation ────────────────────────────────────────────────
  const recalculate = useCallback(() => {
    if (!triggerRef.current || !portalRef.current) return
    const newPos = computePosition(triggerRef.current, portalRef.current, minWidth)
    setPos(newPos)
  }, [triggerRef, minWidth])

  // Recalculate on open and on resize/scroll
  useEffect(() => {
    if (!isOpen) {
      setPos(null)
      return
    }
    // Wait one frame for the portal to mount so we can measure it
    const raf = requestAnimationFrame(() => {
      recalculate()
    })

    const handleResize = () => recalculate()

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true) // capture phase for nested scrolls

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [isOpen, recalculate])

  // ── Auto-close on canvas pan/zoom ─────────────────────────────────────
  useOnViewportChange({
    onStart: useCallback(() => {
      if (isOpen) {
        portalLog('closing on viewport change')
        onClose()
      }
    }, [isOpen, onClose]),
  })

  // ── Click-outside dismissal ────────────────────────────────────────────
  useClickOutside([portalRef, triggerRef], onClose, isOpen)

  if (!isOpen) return null

  return createPortal(
    <div
      ref={portalRef}
      className={`fixed z-[150] nopan nodrag nowheel ${className}`}
      style={{
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        minWidth,
        // Start invisible until position is computed
        visibility: pos ? 'visible' : 'hidden',
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-testid="flyout-portal"
    >
      <div className="bg-surface-1 border border-border rounded-xl shadow-2xl p-3">
        {children}
      </div>
    </div>,
    document.body,
  )
}
